import { css } from '@emotion/react';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonContent,
  IonGrid,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRow,
  IonText,
} from '@ionic/react';
import { notificationsOffCircleOutline, notificationsOutline } from 'ionicons/icons';
import { sortBy } from 'lodash';
import React from 'react';
import { useDeleteAllFollows, useDeleteFollow } from '../../api/mutations/deleteFollow';
import { useGetNetworks } from '../../api/queries/getNetworks';
import CircleUrl from '../../assets/img/circle.svg';
import SquareUrl from '../../assets/img/square.svg';
import { LoginModal } from '../../components/LoginModal';
import PageHeader from '../../components/PageHeader';
import { Spinner } from '../../components/Spinner';
import { Title } from '../../components/Title';
import { useUser } from '../../contexts/UserContext';
import { FollowDto, NetworkId, NetworkSummaryDto } from '../../types';
import { useSettings } from '../../contexts/SettingsContext';

const NotificationPreferencesPage: React.FC = () => {
  const { user, idToken, isUserLoading, follows, isFollowDataLoading } = useUser();
  const deleteFollowMutation = useDeleteFollow();
  const deleteAllFollowsMutation = useDeleteAllFollows();
  const [showLoginModal, setShowLoginModal] = React.useState(false);
  const { data: networksData } = useGetNetworks();
  const { networkId } = useSettings();

  const followByNetwork: Record<NetworkId, FollowDto[]> = React.useMemo(() => {
    return (
      follows?.reduce(
        (acc, follow) => {
          if (!acc[follow.networkId]) {
            acc[follow.networkId] = [];
          }
          acc[follow.networkId].push(follow);
          return acc;
        },
        {} as Record<NetworkId, FollowDto[]>
      ) || {}
    );
  }, [follows]);

  const unfollow = React.useCallback(
    (follow: FollowDto) => {
      if (!idToken) {
        return;
      }
      deleteFollowMutation.mutate({ followId: follow.id, firebaseToken: idToken });
    },
    [deleteFollowMutation, idToken]
  );

  const unfollowAll = React.useCallback(() => {
    if (!idToken) {
      return;
    }
    deleteAllFollowsMutation.mutate({ firebaseToken: idToken });
  }, [deleteAllFollowsMutation, idToken]);

  React.useEffect(() => {
    if (!isUserLoading && !user) {
      setShowLoginModal(true);
    }
  }, [isUserLoading, user]);

  const networkInfoById = React.useMemo(() => {
    if (!networksData) {
      return {};
    }
    return networksData.data.networks.reduce((acc: Record<NetworkId, NetworkSummaryDto>, network) => {
      acc[network.networkId] = network;
      return acc;
    }, {});
  }, [networksData]);

  const notificationPreferencesPageCss = {
    content: css({
      '--padding-top': '1rem',
      '--padding-bottom': '1rem',
      '--padding-start': '1rem',
      '--padding-end': '1rem',
    }),
    card: css({
      margin: 0,
    }),
    logo: css({
      height: '1rem',
      marginRight: '.5rem',
    }),
    iconMarker: css({
      color: '#9cd84e', // US EPA green
      position: 'relative',
      top: '0.25rem',
      marginLeft: '0.2rem',
    }),
    iconBell: css({
      position: 'relative',
      top: '0.25rem',
      marginLeft: '0.2rem',
    }),
    howTolist: css({
      lineHeight: 2,
    }),
  };

  return (
    <IonPage>
      <PageHeader title="Notification Preferences" defaultHref={`/${networkId}`} />
      <IonContent css={notificationPreferencesPageCss.content}>
        {isUserLoading || isFollowDataLoading || !networksData ? (
          <div>
            <Spinner type="circular" />
          </div>
        ) : !user || !idToken ? (
          <LoginModal
            isOpen={showLoginModal}
            setIsOpen={setShowLoginModal}
            title="Login Required"
            promptText="Sign in to manage your notification preferences."
          />
        ) : (
          <>
            <IonText>Hi, {user?.email}!</IonText>

            <br />
            <br />
            <Title>Sites you follow</Title>
            <br />

            {follows && follows.length > 0 ? (
              <IonGrid>
                <IonRow>
                  {Object.entries(followByNetwork).map(([networkId, follows]) => (
                    <IonCol key={networkId} sizeXs="12" sizeSm="6" sizeMd="4" sizeXl="3">
                      <IonCard css={notificationPreferencesPageCss.card}>
                        <IonCardHeader>
                          <IonItem lines="none">
                            <IonImg
                              css={notificationPreferencesPageCss.logo}
                              src={networkInfoById[networkId as NetworkId].logoUrl}
                            />
                            <IonLabel>
                              <Title level={2}>{networkInfoById[networkId as NetworkId].name}</Title>
                            </IonLabel>
                          </IonItem>
                        </IonCardHeader>
                        <IonCardContent>
                          <IonList>
                            {sortBy(follows).map((follow) => (
                              <IonItem key={follow.datasourceId}>
                                <IonLabel>{follow.datasourceName}</IonLabel>
                                <IonButton
                                  slot="end"
                                  fill="clear"
                                  color="danger"
                                  onClick={() => unfollow(follow)}
                                  disabled={deleteFollowMutation.isPending}
                                >
                                  <IonIcon icon={notificationsOffCircleOutline} slot="start"></IonIcon>
                                  Unfollow
                                </IonButton>
                              </IonItem>
                            ))}
                          </IonList>
                        </IonCardContent>
                      </IonCard>
                    </IonCol>
                  ))}
                </IonRow>
                <IonRow>
                  <IonCol size="12">
                    <IonButton
                      fill="solid"
                      color="danger"
                      onClick={() => unfollowAll()}
                      disabled={deleteAllFollowsMutation.isPending}
                    >
                      <IonIcon icon={notificationsOffCircleOutline} slot="start"></IonIcon>
                      Unfollow all
                    </IonButton>
                  </IonCol>
                </IonRow>
              </IonGrid>
            ) : (
              <>
                <IonText>No sites followed yet.</IonText>
              </>
            )}
          </>
        )}

        <br />
        <br />
        <Title>How to follow a site</Title>
        <ol css={notificationPreferencesPageCss.howTolist}>
          <li>
            Select your site of interest by clicking on a map pin:
            <IonIcon src={CircleUrl} css={notificationPreferencesPageCss.iconMarker} size="small" />
            <IonIcon src={SquareUrl} css={notificationPreferencesPageCss.iconMarker} size="small" />
          </li>
          <li>
            Click on the bell icon next to the site name
            <IonIcon icon={notificationsOutline} css={notificationPreferencesPageCss.iconBell} size="small" />
          </li>
          <li>Log in or sign up if prompted</li>
        </ol>

        <IonText>You will receive notifications if air quality becomes unhealthy.</IonText>
      </IonContent>
    </IonPage>
  );
};

export default NotificationPreferencesPage;
